<template>
  <AuthLayout>
    <!-- Validando token -->
    <div v-if="validating" class="state-msg">
      <i class="pi pi-spin pi-spinner spinner-icon" aria-hidden="true" />
      <p>Verificando enlace...</p>
    </div>

    <!-- Token inválido -->
    <div v-else-if="tokenInvalid" class="state-msg">
      <i class="pi pi-times-circle state-icon state-icon--error" aria-hidden="true" />
      <h3>Enlace inválido o expirado</h3>
      <p>El enlace de recuperación no es válido o ya expiró (válido por 2 horas).</p>
      <Button
        label="Solicitar nuevo enlace"
        icon="pi pi-refresh"
        class="w-full"
        @click="$router.push('/forgot-password')"
      />
    </div>

    <!-- Formulario de nueva contraseña -->
    <form v-else-if="!success" class="auth-form" novalidate @submit.prevent="submit">
      <div class="auth-header">
        <h2>Nueva contraseña</h2>
        <p v-if="userEmail">Para la cuenta <strong>{{ userEmail }}</strong></p>
      </div>

      <div class="field">
        <label>Nueva contraseña <span class="req">*</span></label>
        <PasswordField v-model="newPassword" :auto-generate="false" placeholder="Mínimo 8 caracteres" />
        <small v-if="errors.password" class="field-error">
          <i class="pi pi-exclamation-circle" aria-hidden="true" /> {{ errors.password }}
        </small>
      </div>

      <div class="field">
        <label>Confirmar contraseña <span class="req">*</span></label>
        <Password
          v-model="confirmPassword"
          placeholder="Repite la contraseña"
          :feedback="false"
          toggle-mask
          :input-props="{ autocomplete: 'new-password' }"
          :invalid="!!errors.confirm"
          @input="errors.confirm = ''"
        />
        <small v-if="errors.confirm" class="field-error">
          <i class="pi pi-exclamation-circle" aria-hidden="true" /> {{ errors.confirm }}
        </small>
      </div>

      <Message v-if="submitError" severity="error" :closable="false">{{ submitError }}</Message>

      <Button
        label="Restablecer contraseña"
        icon="pi pi-lock"
        type="submit"
        class="w-full"
        :loading="loading"
      />
    </form>

    <!-- Éxito -->
    <div v-else class="state-msg">
      <i class="pi pi-check-circle state-icon state-icon--success" aria-hidden="true" />
      <h3>¡Contraseña restablecida!</h3>
      <p>Tu contraseña ha sido actualizada correctamente.</p>
      <Button
        label="Iniciar sesión"
        icon="pi pi-sign-in"
        class="w-full"
        @click="$router.push('/login')"
      />
    </div>
  </AuthLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import Password from 'primevue/password';
import Button from 'primevue/button';
import Message from 'primevue/message';
import PasswordField from '../components/common/PasswordField.vue';
import AuthLayout from '../components/layout/AuthLayout.vue';
import api from '../services/api';

const route = useRoute();
const token = route.query.token || '';

const validating = ref(true);
const tokenInvalid = ref(false);
const success = ref(false);
const loading = ref(false);
const userEmail = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const errors = ref({});
const submitError = ref('');

// Validar el token al cargar la página
onMounted(async () => {
  if (!token) { tokenInvalid.value = true; validating.value = false; return; }
  try {
    const res = await api.post('/auth/validate-reset-token', { token });
    userEmail.value = res.data.email;
  } catch (e) {
    tokenInvalid.value = true;
  } finally {
    validating.value = false;
  }
});

const validate = () => {
  errors.value = {};
  if (!newPassword.value || newPassword.value.length < 8)
    errors.value.password = 'La contraseña debe tener al menos 8 caracteres';
  if (newPassword.value !== confirmPassword.value)
    errors.value.confirm = 'Las contraseñas no coinciden';
  return Object.keys(errors.value).length === 0;
};

const submit = async () => {
  if (!validate()) return;
  loading.value = true; submitError.value = '';
  try {
    await api.post('/auth/reset-password', { token, new_password: newPassword.value });
    success.value = true;
  } catch (e) {
    submitError.value = e.response?.data?.detail || 'Error al restablecer la contraseña';
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.auth-header {
  margin-bottom: 0.25rem;
}

.auth-header h2 {
  margin: 0 0 0.3rem;
  color: #1e293b;
  font-size: 1.45rem;
  font-weight: 700;
}

.auth-header p {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.field label {
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
}

.field :deep(.p-inputtext) {
  width: 100%;
}

.field-error {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: #dc2626;
  font-size: 0.78rem;
}

.req {
  color: #ef4444;
}

.w-full {
  width: 100%;
}

.state-msg {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
  padding: 0.5rem 0;
}

.state-msg h3 {
  margin: 0;
  color: #1e293b;
}

.state-msg p {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.state-msg .w-full {
  margin-top: 0.25rem;
}

.spinner-icon {
  font-size: 2rem;
  color: #6366f1;
}

.state-icon {
  font-size: 3rem;
}

.state-icon--error {
  color: #ef4444;
}

.state-icon--success {
  color: #10b981;
  font-size: 3.5rem;
}
</style>
